import { makeObservable } from "mobx";

import { BaseNodeModel } from "@/models/nodes/BaseNodeModel";
import { SymbolNodeModel } from "@/models/nodes/SymbolNodeModel";
import { UnitNodeModel } from "@/models/nodes/UnitNodeModel";
import { getRandomTailwindColor } from "@/utils/tailwindColors";

import type { DocumentModel } from "@/models/DocumentModel";
import type { NodeModel, NodeId } from "@/models/nodes/types";
import type { VisitableNode, NodeVisitor } from "@/models/nodes/visitors/NodeVisitor";
import type { TailwindColor } from "@/utils/tailwindColors";
import type { ConstantNodeProps, ConstantNodeConstructorProps } from "@corca/data";

export class ConstantNodeModel extends BaseNodeModel<ConstantNodeProps> implements VisitableNode {
  static type = "constant" as const;

  type = ConstantNodeModel.type;

  protected fillRequiredProps(props: ConstantNodeConstructorProps): ConstantNodeProps {
    const color = ConstantNodeModel.getConstantColor(this.doc, props, this.symbolNode?.value);
    return { ...props, color: props.color ?? color };
  }

  static getConstantByValue(doc: DocumentModel, value?: string) {
    if (!value) {
      return undefined;
    }
    const constants = doc.getNodesByType(ConstantNodeModel.type);
    return constants.find((node) => node.value === value);
  }

  static getConstantColor(
    doc: DocumentModel,
    props: ConstantNodeConstructorProps,
    value?: string,
  ): TailwindColor {
    return (
      props.color ||
      ConstantNodeModel.getConstantByValue(doc, value)?.color ||
      getRandomTailwindColor(value, "slate")
    );
  }

  constructor(...args: ConstructorParameters<typeof BaseNodeModel<ConstantNodeConstructorProps>>) {
    super(...args);
    makeObservable(this, {
      symbolNode: true,
      value: true,
      unitNode: true,
    });
  }

  static create(
    doc: DocumentModel,
    value: string | SymbolNodeModel,
    props: ConstantNodeConstructorProps = {},
    nodes: NodeModel[] = [],
    id?: NodeId,
  ) {
    const symbolNode = typeof value === "string" ? doc.create(SymbolNodeModel, value) : value;
    const color = ConstantNodeModel.getConstantColor(doc, props, symbolNode.value);

    return new ConstantNodeModel(doc, id, { ...props, color }, [symbolNode, ...nodes]);
  }

  get isEditable(): boolean {
    return true;
  }

  get symbolNode() {
    return this.descendants.find((n) => n.is(SymbolNodeModel));
  }

  get unitNode() {
    return this.children.find((n) => n.is(UnitNodeModel));
  }

  get value(): string | undefined {
    return this.symbolNode?.props.value;
  }

  set value(value: string) {
    if (this.symbolNode) {
      this.symbolNode.value = value;
    }
  }

  get name(): string {
    return this.props.name || "";
  }

  set name(value: string) {
    this.updateProps({ name: value });
  }

  get color(): TailwindColor {
    return this.props.color;
  }

  set color(value: TailwindColor) {
    this.updateProps({ color: value });
  }

  accept<R>(visitor: NodeVisitor<R>): R {
    return visitor.visitConstantNode(this);
  }
}
